import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import "../Css/Homepage.css";

export default function Homepage({ children = "Explore Now ↗" }) {
  const [isHovered, setIsHovered] = useState(false);
  const [particles, setParticles] = useState([]);
  const [cornerParticles, setCornerParticles] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const buttonRef = useRef(null);
  const heroSectionRef = useRef(null);
  const navigate = useNavigate();

  // Words to cycle through with their colors
  const rotatingWords = [
    { text: "Creativity", color: "#36c5f0", bgColor: "#e4f5ffff" },
    { text: "Innovative", color: "#dc2626", bgColor: "#fecaca" },
    { text: "Team Work", color: "#059669", bgColor: "#a7f3d0" },
    { text: "Productivity", color: "#d97706", bgColor: "#fed7aa" },
    { text: "Reliability", color: "#7c3aed", bgColor: "#ddd6fe" }
  ];

  // Auto-rotate words effect with animation states
  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentWordIndex((prevIndex) => 
          prevIndex === rotatingWords.length - 1 ? 0 : prevIndex + 1
        );
        setTimeout(() => {
          setIsAnimating(false);
        }, 100);
      }, 500); // Half of the animation duration
    }, 2500); // Change word every 2.5 seconds

    return () => clearInterval(interval);
  }, [rotatingWords.length]);

  const generateParticles = (count, x, y) => {
    const newParticles = [];
    for (let i = 0; i < count; i++) {
      newParticles.push({
        id: Math.random(),
        x: x || Math.random() * 100,
        y: y || Math.random() * 100,
        size: Math.random() * 4 + 2,
        duration: Math.random() * 1 + 0.5,
        delay: Math.random() * 0.5
      });
    }
    return newParticles;
  };

  const generateCornerParticles = (count, buttonRect) => {
    if (!buttonRect) return [];
    
    const newParticles = [];
    
    for (let i = 0; i < count; i++) {
      // Start from the bottom-right corner where the cut is
      const startX = buttonRect.right - 8;
      const startY = buttonRect.bottom - 8;
      
      // Create a downward and outward flow from the bottom-right corner
      const angle = Math.random() * Math.PI / 3 + Math.PI / 6; // 30-90 degrees
      const distance = 80 + Math.random() * 200;
      
      const endX = startX + Math.cos(angle) * distance;
      const endY = startY + Math.sin(angle) * distance;
      
      // Curved path points
      const midX1 = startX + Math.cos(angle) * distance * 0.2;
      const midY1 = startY + Math.sin(angle) * distance * 0.2;
      const midX2 = startX + Math.cos(angle) * distance * 0.5;
      const midY2 = startY + Math.sin(angle) * distance * 0.5;
      const midX3 = startX + Math.cos(angle) * distance * 0.8;
      const midY3 = startY + Math.sin(angle) * distance * 0.8;
      
      const speedTypes = ['slow', 'medium', 'fast'];
      const speed = speedTypes[Math.floor(Math.random() * speedTypes.length)];
      
      const shapeTypes = ['circle', 'blob', 'drop'];
      const shape = shapeTypes[Math.floor(Math.random() * shapeTypes.length)];
      
      // Use only the violet colors from the button with opacity variations
      const baseColors = [
        'rgba(72, 52, 212, 0.9)',  // #4834D4
        'rgba(104, 109, 224, 0.8)', // #686DE0
        'rgba(91, 33, 182, 0.7)',   // #5b21b6
      ];
      const color = baseColors[Math.floor(Math.random() * baseColors.length)];
      
      newParticles.push({
        id: Math.random(),
        startX: `${startX}px`,
        startY: `${startY}px`,
        endX: `${endX}px`,
        endY: `${endY}px`,
        midX1: `${midX1}px`,
        midY1: `${midY1}px`,
        midX2: `${midX2}px`,
        midY2: `${midY2}px`,
        midX3: `${midX3}px`,
        midY3: `${midY3}px`,
        size: Math.random() * 5 + 2,
        color: color,
        speed: speed,
        shape: shape,
        opacity: Math.random() * 0.7 + 0.3
      });
    }
    return newParticles;
  };

  const handleMouseEnter = (e) => {
    setIsHovered(true);
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      setParticles(generateParticles(8, 50, 50));
      
      // Generate initial corner particles
      setCornerParticles(generateCornerParticles(6, buttonRect));
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setTimeout(() => {
      setParticles([]);
      setTimeout(() => setCornerParticles([]), 1000);
    }, 500);
  };

  const handleExploreClick = () => {
    // Use navigation state to trigger scroll in Main component
    navigate('/', { state: { scrollTo: 'services' } });
  };

  useEffect(() => {
    if (isHovered && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      
      // Regular particles interval
      const particleInterval = setInterval(() => {
        setParticles(prev => [...prev, ...generateParticles(2)]);
      }, 200);
      
      // Corner particles interval - continuous leak from bottom-right
      const cornerParticleInterval = setInterval(() => {
        setCornerParticles(prev => {
          // Remove old particles that have finished animating
          const updatedParticles = prev.filter(p => 
            Date.now() - p.createdAt < 4000
          );
          
          // Add new particles
          const newParticles = generateCornerParticles(2, buttonRect).map(p => ({
            ...p,
            createdAt: Date.now()
          }));
          
          return [...updatedParticles, ...newParticles];
        });
      }, 300);
      
      return () => {
        clearInterval(particleInterval);
        clearInterval(cornerParticleInterval);
      };
    }
  }, [isHovered]);

  // Update particle positions on scroll/resize
  useEffect(() => {
    const updateParticles = () => {
      if (isHovered && buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        setCornerParticles(prev => 
          prev.map(particle => ({
            ...particle,
            startX: `${buttonRect.right - 8}px`,
            startY: `${buttonRect.bottom - 8}px`
          }))
        );
      }
    };

    window.addEventListener('scroll', updateParticles);
    window.addEventListener('resize', updateParticles);

    return () => {
      window.removeEventListener('scroll', updateParticles);
      window.removeEventListener('resize', updateParticles);
    };
  }, [isHovered]);

  return (
    <div 
      style={{ 
        backgroundColor: '#fff', 
        position: 'relative',
        overflow: 'hidden',
        
      }}
      className='mb-5'
      ref={heroSectionRef}
    >
      {/* Bottom Corner Leak Particles */}
      {cornerParticles.map(particle => (
        <div
          key={particle.id}
          className={`bottom-corner-leak-particle bottom-corner-leak-particle--${particle.speed} bottom-corner-leak-particle--${particle.shape}`}
          style={{
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            background: particle.color,
            left: 0,
            top: 0,
            '--start-x': particle.startX,
            '--start-y': particle.startY,
            '--mid-x1': particle.midX1,
            '--mid-y1': particle.midY1,
            '--mid-x2': particle.midX2,
            '--mid-y2': particle.midY2,
            '--mid-x3': particle.midX3,
            '--mid-y3': particle.midY3,
            '--end-x': particle.endX,
            '--end-y': particle.endY,
            opacity: particle.opacity
          }}
        />
      ))}
      
      <div className="container">
        {/* Hero Section */}
        <div className="text-center mb-5">
          <h1 style={{ 
            fontSize: 'clamp(2rem, 5vw, 3.5rem)', 
            fontWeight: 'bold',
            color: '#000',
            marginBottom: '20px',
            lineHeight: '1.3'
          }}>
           We craft end-to-end solutions from concept to final product with {" "}
           <span 
             className={`rotating-word rotating-word--bounce ${isAnimating ? 'rotating-word--animating' : ''}`}
             style={{
               backgroundColor: rotatingWords[currentWordIndex].bgColor,
               color: rotatingWords[currentWordIndex].color,
               display: 'inline-flex',
               alignItems: 'center',
               justifyContent: 'center',
               height: 'clamp(40px, 8vw, 70px)',
               padding: '2px 12px',
               borderRadius: '16px',
               lineHeight: '1',
               minWidth: 'max-content'
             }}
           >
             <span className="rotating-word__shimmer"></span>
             <span className={`rotating-word__text ${isAnimating ? 'rotating-word__text--exiting' : 'rotating-word__text--entering'}`}>
               {rotatingWords[currentWordIndex].text}
             </span>
           </span>
            <br className="d-none d-md-block" />
          </h1>
          
          <h4 style={{ 
            fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)', 
            color: '#645E80',
            margin: '0 auto 50px',
            lineHeight: '1.6',
            fontWeight: 500,
            padding: '0 15px',
            textAlign: 'center',
            maxWidth: '47ch',
            fontSize: '25px'
          }}
          className="mx-auto"
          >
            We excel in avant-garde technologies
          </h4>

          <button
            ref={buttonRef}
            className={`modern-btn ${isHovered ? 'modern-btn--hovered' : ''}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleExploreClick}
          >
            <span className="modern-btn__text">{children}</span>
            
            {/* Animated border elements */}
            <span className="modern-btn__border modern-btn__border--top"></span>
            <span className="modern-btn__border modern-btn__border--right"></span>
            <span className="modern-btn__border modern-btn__border--bottom"></span>
            <span className="modern-btn__border modern-btn__border--left"></span>
            
            {/* Floating particles */}
            <div className="modern-btn__particles">
              {particles.map(particle => (
                <span
                  key={particle.id}
                  className="modern-btn__particle"
                  style={{
                    left: `${particle.x}%`,
                    top: `${particle.y}%`,
                    width: `${particle.size}px`,
                    height: `${particle.size}px`,
                    animationDuration: `${particle.duration}s`,
                    animationDelay: `${particle.delay}s`
                  }}
                />
              ))}
            </div>
            
            {/* Glow effect */}
            <span className="modern-btn__glow"></span>
          </button>
        </div>

        {/* Floating Stats Cards - Hidden on mobile */}
        <div style={{ position: 'relative' }} className='d-none d-lg-block'>
          {/* Top Left - Total Projects */}
          <div style={{
            position: 'absolute',
            top: '-200px',
            left: '0',
            zIndex: 10
          }}>
            <img src="/images/total-projects.png" alt="Total Projects" style={{ width: 'auto', maxWidth: '100%' }} />
          </div>

          {/* Top Right - Satisfied Clients */}
          <div style={{
            position: 'absolute',
            top: '-200px',
            right: '0',
            zIndex: 10
          }}>
            <img src="/images/Clients-Satisfied.png" alt="Satisfied Clients" style={{ width: 'auto', maxWidth: '100%' }} />

          </div>

          {/* Bottom Right - Daily Visitors */}
          <div style={{
            position: 'absolute',
            right: '110px',
            zIndex: 10,
            top: '-30px',
          }}>
            <img src="/images/daily-visitors.png" alt="Daily Visitor" style={{ width: 'auto', maxWidth: '100%' }} />

          </div>

          {/* Decorative Elements */}
          <div style={{
            position: 'absolute',
            bottom: '-100px',
            right: '350px',
            zIndex: 5
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderLeft: '3px solid #ffc0cb',
              borderBottom: '3px solid #ffc0cb',
              transform: 'rotate(45deg)'
            }}></div>
          </div>
        </div>

        {/* Bottom Analytics Cards - Hidden on mobile, visible on medium devices and up */}
        <div className="row justify-content-center d-none d-md-flex">

          <div className="col-12 col-md-4 mb-4" style={{ position: 'relative' }}>
          <img src="/images/plus.png" alt="Plus" style={{ width: 'auto', maxWidth: '100%',marginTop:"80px" }} />

  {/* Dot image behind */}
  <img
    src="/images/Dot.png"
    alt="Dot"
    style={{
      position: 'absolute',
      right: '220px',
      top: '70px',
      zIndex: 0, // behind
    }}
  />

  {/* Status image in front */}
  <img
    src="/images/company-growth.png"
    alt="Process Stats"
    className="img-fluid"
    style={{
      position: 'relative',
      zIndex: 1, // in front
      top:"-20px"
    }}
  />
</div>
          <div className="col-12 col-md-4 mb-4">
            <img src="/images/product-analytics.png" alt="Product Analytics" className="img-fluid" />
          </div>
<div className="col-12 col-md-4 mb-4" style={{ position: 'relative' }}>
  {/* Dot image behind */}
  <img
    src="/images/Dot.png"
    alt="Dot"
    style={{
      position: 'absolute',
      right: '195px',
      top: '55px',
      zIndex: 0, // behind
    }}
  />

  {/* Status image in front */}
  <img
    src="/images/process-status.png"
    alt="Process Stats"
    className="img-fluid"
    style={{
      position: 'relative',
      zIndex: 1, // in front
      top:"-20px"
    }}
  />
<div style={{ position: 'relative', display: 'inline-block',marginLeft:'117px' }}>
  <img
    src="/images/Vector.png"
    alt="Vector"
    style={{
      width: 'auto',
      position: 'relative',
      zIndex: 1, // lower
    }}
  />
  <img
    src="/images/triangle.png"
    alt="Triangle"
    style={{
      width: 'auto',
      position: 'absolute',
      top: 0,
      left: -20,
      zIndex: 2, // higher (on top)
    }}
  />
</div>


</div>

        </div>

      </div>
    </div>
  );
}